// ═══════════════════════════════════════════════════════════════════
//  modules/shop/ShopPage.js
//  ShopPage · Magasin · le prix du jour, pas le prix du catalogue
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { erreurDe, horodate, nomObjet } from "../ah/ah";
import { argent, dateCourte, nombre } from "../erp/metiers";

// ⚠ CE QUE CETTE PAGE MONTRE ET QU'AUCUN ÉCRAN N'AVAIT
//
// `os_article` ne contient PAS les prix que les joueurs paient. Ce sont des
// prix de référence. Le marché (§ 3) les multiplie en permanence par un
// coefficient calculé sur le stock virtuel :
//
//     r = borner(stock / stock_reference, 0,25 .. 4)
//     m = borner(r ^ (−élasticité), 0,5 .. 2)
//     achat   = MAX(plancher, prix_achat × m)
//     revente = prix_revente × m
//
// Ouvrir la table dans HeidiSQL affiche donc un prix que personne ne paie.
// Le back refait le calcul du plugin ; la page montre les DEUX, et la
// colonne « auj. » est celle qui compte.
export function ShopPage({ token, user }) {
  const [vue, setVue] = useState("catalogue");
  const admin = user && (user.role === "super_admin" || user.role === "admin");

  const vues = [
    { id: "catalogue", label: "🏷️ Catalogue et prix du jour" },
    { id: "journal", label: "📜 Journal" },
    { id: "refus", label: "🚫 Refusés au chargement" },
    ...(admin ? [{ id: "plafonds", label: "🪣 Plafonds à vie" }] : []),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>🛒 Magasin</h1>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>
          lecture seule — les prix affichés « auj. » sont recalculés, pas lus
        </span>
      </div>

      <ShopResume token={token} />

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}`, flexWrap: "wrap" }}>
        {vues.map((v) => (
          <button key={v.id} onClick={() => setVue(v.id)}
            style={{
              background: vue === v.id ? THEME.panel : "transparent",
              border: `1px solid ${vue === v.id ? THEME.border : "transparent"}`,
              borderBottom: vue === v.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
              color: vue === v.id ? THEME.text : THEME.textMuted,
              borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer",
              fontSize: 13, fontWeight: vue === v.id ? 600 : 400, marginBottom: -1,
            }}>{v.label}</button>
        ))}
      </div>

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 260 }}>
        {vue === "catalogue" && <Catalogue token={token} />}
        {vue === "journal" && <Journal token={token} />}
        {vue === "refus" && <Refus token={token} />}
        {vue === "plafonds" && admin && <Plafonds token={token} />}
      </div>
    </div>
  );
}

const champ = {
  background: THEME.surface, border: `1px solid ${THEME.border}`,
  color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
};
const th = {
  color: THEME.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
  textAlign: "left", padding: "6px 8px", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap",
};
const td = { color: THEME.text, fontSize: 12, padding: "6px 8px", borderBottom: `1px solid ${THEME.border}` };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

// Le garde qui a coûté une page blanche sur l'hôtel des ventes : un
// `data.somme.unites` lu sans vérifier `data.error` fait planter React, et
// un plantage démonte TOUT l'arbre — l'utilisateur ne voit pas une erreur,
// il ne voit rien du tout.
function Erreur({ texte }) {
  return (
    <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⛔ La route n'a pas répondu correctement</div>
      <code style={{ color: THEME.text, fontSize: 12, wordBreak: "break-word" }}>{texte}</code>
    </div>
  );
}

function Indisponible() {
  return (
    <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
      ⏳ Les tables <code>os_*</code> n'existent pas sur cette base. Elles sont créées
      au premier démarrage du plugin OrialisShop.
    </div>
  );
}

// « 1 ligne(s) » dans une alarme donne l'air d'un brouillon, et une alarme
// qui a l'air d'un brouillon n'est pas prise au sérieux.
function pluriel(n, singulier, plurielMot) {
  return nombre(n) + " " + (Math.abs(Number(n)) > 1 ? (plurielMot || singulier + "s") : singulier);
}

function Vide({ texte }) {
  return <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>{texte}</div>;
}

function Compteur({ label, valeur, detail, couleur }) {
  return (
    <div style={{
      background: THEME.surface, border: `1px solid ${couleur || THEME.border}`,
      borderRadius: 8, padding: "10px 14px", minWidth: 128,
    }}>
      <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: couleur || THEME.text, fontSize: 19, fontWeight: 800, marginTop: 2 }}>{valeur}</div>
      {detail && <div style={{ color: THEME.textMuted, fontSize: 10, marginTop: 2 }}>{detail}</div>}
    </div>
  );
}

function libelleArticle(l) {
  // Priorité au nom du catalogue : c'est le seul endroit du projet où un
  // nom français est SAISI. Le dictionnaire ne sert que de repli.
  if (l.nom) return l.nom;
  return nomObjet(l.identifiant);
}

// ═══════════════════════════════════════════════════════════════════════
//  LE RÉSUMÉ — au-dessus des onglets, il est le diagnostic
// ═══════════════════════════════════════════════════════════════════════
function ShopResume({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/shop/resume`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch((e) => console.error(e));
  }, [token]);

  if (!data) return <div style={{ color: THEME.textMuted, fontSize: 12 }}>Chargement...</div>;
  if (data.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const cat = data.catalogue || {};
  // `os_journal.sens` porte QUATRE libellés, vérifiés dans Transaction.java :
  // ACHAT et ACHAT_PANIER d'un côté (le joueur paie), REVENTE et
  // VENTE_COFFRE de l'autre (le magasin verse). Le back range chaque ligne
  // dans une famille et laisse `famille: null` sur tout libellé qu'il ne
  // connaît pas — un libellé nouveau ne sera donc jamais rangé de force du
  // mauvais côté du solde.
  const cumul = (f) => (data.sens_7j || [])
    .filter((s) => s.famille === f)
    .reduce((t, s) => ({ lignes: t.lignes + s.lignes, montant: t.montant + s.montant, unites: t.unites + s.unites }),
            { lignes: 0, montant: 0, unites: 0 });
  const achats = cumul("sortant");
  const ventes = cumul("entrant");
  const inconnus = (data.sens_7j || []).filter((s) => !s.famille);
  const soldeCalculable = achats.lignes > 0 || ventes.lignes > 0;
  // Positif = le magasin a pris plus qu'il n'a donné ; négatif = il injecte
  // de l'argent dans l'économie.
  const solde = achats.montant - ventes.montant;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* ── L'alarme du § 4.2 passe AVANT les compteurs ─────────────── */}
      {cat.inversions > 0 && (
        <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            ⛔ {pluriel(cat.inversions, "article")} où la revente est ≥ à l'achat
          </div>
          Le § 4.2 dit <code>revente &lt; achat</code>. Le marché ne peut pas inverser ça — il applique
          le même coefficient aux deux — donc c'est une valeur de catalogue. Un joueur qui achète
          et revend en boucle gagne de l'argent sans rien faire. Onglet <b>Catalogue</b>, filtre
          « à surveiller ».
        </div>
      )}

      {inconnus.length > 0 && (
        <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
          <b>⚠ Libellé de sens inconnu dans <code>os_journal</code> :</b>{" "}
          {inconnus.map((s) => <code key={s.sens} style={{ marginRight: 8 }}>{s.sens} ({s.lignes})</code>)}
          <div style={{ marginTop: 4 }}>
            Le plugin en écrit quatre : <code>ACHAT</code>, <code>ACHAT_PANIER</code>,{" "}
            <code>REVENTE</code>, <code>VENTE_COFFRE</code>. Celui-ci n'en fait pas partie —
            il n'est donc <b>pas compté dans le solde</b>, plutôt que d'être rangé au hasard
            du bon ou du mauvais côté. Dis-le-moi et je le range.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Compteur label="Articles" valeur={nombre(cat.articles)}
          detail={`${nombre(cat.categories)} catégories · ${nombre(cat.achetables)} achetables`} />
        <Compteur label="Aujourd'hui" valeur={nombre(data.jour ? data.jour.lignes : 0)}
          detail={data.jour ? argent(data.jour.montant) : "—"} />
        {/* Si les libellés ne sont pas ceux attendus, ces deux-là afficheraient
            « 0 » alors qu'il y a des lignes. Un zéro faux est pire qu'une
            case absente : on ne va pas le vérifier. */}
        <Compteur label="Achats 7 j" valeur={nombre(achats.lignes)}
          detail={achats.lignes ? `${argent(achats.montant)} · ${nombre(achats.unites)} u` : "aucun"} />
        <Compteur label="Reventes 7 j" valeur={nombre(ventes.lignes)}
          detail={ventes.lignes ? `${argent(ventes.montant)} · ${nombre(ventes.unites)} u` : "aucune"} />
        {inconnus.map((s) => (
          <Compteur key={s.sens} label={`${s.sens} 7 j`} valeur={nombre(s.lignes)}
            couleur={THEME.amber}
            detail={`${argent(s.montant)} · ${nombre(s.unites)} u`} />
        ))}
        {soldeCalculable && (
          <Compteur label="Solde 7 j" valeur={argent(solde)}
            couleur={solde < 0 ? THEME.amber : THEME.emerald}
            detail={solde < 0 ? "le magasin injecte" : "le magasin retire"} />
        )}
        <Compteur label="Refusés" valeur={nombre(data.refus ? data.refus.lignes : 0)}
          couleur={data.refus && data.refus.lignes > 0 ? THEME.amber : undefined}
          detail={data.refus && data.refus.derniers ? dateCourte(data.refus.derniers) : "aucun"} />
        <Compteur label="Plafonds à vie" valeur={nombre(data.vie ? data.vie.lignes : 0)}
          detail={data.vie ? pluriel(data.vie.joueurs, "joueur") : "—"} />
      </div>

      {/* ── La question du § 8.6.6, qu'on ne pouvait pas trancher sans
             regarder : l'AH devait-il réutiliser cette boîte ? ────────── */}
      {(data.retraits || []).length > 0 && (
        <div style={{ color: THEME.textMuted, fontSize: 11, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <b style={{ color: THEME.textDim }}>Boîte de retrait :</b>
          {data.retraits.map((r) => (
            <span key={r.source || "?"}>
              <code style={{ color: THEME.text }}>{r.source || "(vide)"}</code>{" "}
              {pluriel(r.lignes, "ligne")} · {nombre(r.unites)} u · {pluriel(r.joueurs, "joueur")}
              {r.plus_ancien ? ` · plus ancien ${dateCourte(r.plus_ancien)}` : ""}
            </span>
          ))}
          {!data.retraits.some((r) => r.source === "ah") && (
            <span style={{ color: THEME.amber }}>
              — aucune ligne « ah » : l'hôtel des ventes utilise bien sa propre <code>oa_boite</code>.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LE CATALOGUE — la vue qui justifie le module
// ═══════════════════════════════════════════════════════════════════════
function Catalogue({ token }) {
  const [f, setF] = useState({ categorie: "", q: "", etat: "", tri: "rangement", alerte: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/shop/catalogue?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, f, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement du catalogue...</div>;
  if (data && data.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const lignes = data.lignes || [];
  const auPlancher = lignes.filter((l) => l.au_plancher).length;
  const inversions = lignes.filter((l) => l.inversion).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...champ, minWidth: 180 }} placeholder="nom ou identifiant..."
          value={f.q} onChange={(e) => changer("q", e.target.value)} />
        <select style={champ} value={f.categorie} onChange={(e) => changer("categorie", e.target.value)}>
          <option value="">toutes catégories</option>
          {(data.categories || []).map((c) => (
            <option key={c.categorie} value={c.categorie}>{c.categorie} ({c.total})</option>
          ))}
        </select>
        <select style={champ} value={f.etat} onChange={(e) => changer("etat", e.target.value)}>
          <option value="">tous états</option>
          <option value="achat">achetables</option>
          <option value="vente">revendables</option>
          <option value="inactif">inactifs</option>
        </select>
        <select style={champ} value={f.tri} onChange={(e) => changer("tri", e.target.value)}>
          <option value="rangement">rangement du menu</option>
          <option value="nom">nom</option>
          <option value="achat">prix d'achat ↓</option>
          <option value="revente">prix de revente ↓</option>
          <option value="marge">marge ↓</option>
        </select>
        <button onClick={() => changer("alerte", f.alerte === "1" ? "" : "1")}
          style={{
            ...champ, cursor: "pointer",
            borderColor: f.alerte === "1" ? THEME.amber : THEME.border,
            color: f.alerte === "1" ? THEME.amber : THEME.textMuted,
            fontWeight: f.alerte === "1" ? 700 : 400,
          }}>⚠ à surveiller</button>
        <span style={{ color: THEME.textMuted, fontSize: 11, marginLeft: "auto" }}>
          {pluriel(data.total, "article")} · plancher d'achat {argent(data.plancher)}
        </span>
      </div>

      {/* ── Le rappel de ce que la table ne dit pas ─────────────────── */}
      <div style={{ color: THEME.textMuted, fontSize: 11, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "8px 12px" }}>
        <b style={{ color: THEME.textDim }}>réf.</b> = le prix écrit dans <code>os_article</code>, que personne ne paie.
        {"  "}<b style={{ color: THEME.textDim }}>auj.</b> = ce prix multiplié par le marché — le <b>plein tarif</b> du jour.
        {"  "}En jeu il baisse encore de la remise de grade ou de staff du joueur, et du taux vedette :
        ce qui a vraiment été appliqué se lit ligne par ligne dans le Journal.
        {"  "}Sur cette page : {nombre(auPlancher)} au plancher, {pluriel(inversions, "inversion")}.
      </div>

      {lignes.length === 0 ? <Vide texte="Aucun article ne correspond." /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Article</th>
                <th style={th}>Catégorie</th>
                <th style={{ ...th, textAlign: "right" }}>Achat réf.</th>
                <th style={{ ...th, textAlign: "right" }}>Achat auj.</th>
                <th style={{ ...th, textAlign: "right" }}>Revente réf.</th>
                <th style={{ ...th, textAlign: "right" }}>Revente auj.</th>
                <th style={{ ...th, textAlign: "right" }} title="Le coefficient du marché : ce par quoi les deux prix de référence sont multipliés aujourd'hui.">Marché</th>
                <th style={{ ...th, textAlign: "right" }}>Stock</th>
                <th style={th}>État</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id} style={{ background: l.inversion ? THEME.redDim : "transparent" }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{libelleArticle(l)}</div>
                    <code style={{ color: THEME.textMuted, fontSize: 10 }}>{l.identifiant}</code>
                  </td>
                  <td style={{ ...td, color: THEME.textMuted, fontSize: 11 }}>
                    {l.categorie}{l.sous_categorie ? ` / ${l.sous_categorie}` : ""}
                  </td>
                  <td style={{ ...tdNum, color: THEME.textMuted }}>{l.prix_achat === null ? "—" : argent(l.prix_achat)}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: l.au_plancher ? THEME.amber : THEME.text }}>
                    {l.achat_jour === null ? "—" : argent(l.achat_jour)}
                  </td>
                  <td style={{ ...tdNum, color: THEME.textMuted }}>{l.prix_revente === null ? "—" : argent(l.prix_revente)}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: l.inversion ? THEME.red : THEME.text }}>
                    {l.revente_jour === null ? "—" : argent(l.revente_jour)}
                  </td>
                  <td style={{
                    ...tdNum,
                    color: l.multiplicateur > 1.02 ? THEME.red : l.multiplicateur < 0.98 ? THEME.emerald : THEME.textMuted,
                  }}>{nombre(l.multiplicateur, 2)}</td>
                  <td style={tdNum}>
                    {l.stock === null ? <span style={{ color: THEME.textMuted }}>jamais</span> : nombre(l.stock)}
                    {l.tension !== null && (
                      <div style={{ color: THEME.textMuted, fontSize: 10 }}>{nombre(l.tension, 1)} %</div>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {l.inversion && <Etiquette texte="revente ≥ achat" couleur={THEME.red} fond={THEME.redDim} />}
                      {l.au_plancher && <Etiquette texte="au plancher" couleur={THEME.amber} fond={THEME.amberDim} />}
                      {!l.achat_actif && <Etiquette texte="achat off" couleur={THEME.textMuted} fond={THEME.surface} />}
                      {!l.vente_actif && <Etiquette texte="revente off" couleur={THEME.textMuted} fond={THEME.surface} />}
                      {l.plafond_vie !== null && <Etiquette texte={`vie ${l.plafond_vie}`} couleur={THEME.purple} fond={THEME.purpleDim} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={data.page} pages={data.pages} setPage={setPage} />
    </div>
  );
}

function Etiquette({ texte, couleur, fond }) {
  return (
    <span style={{
      color: couleur, background: fond, border: `1px solid ${couleur}`,
      borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
    }}>{texte}</span>
  );
}

function Pagination({ page, pages, setPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
      <button style={{ ...champ, cursor: page > 1 ? "pointer" : "default", opacity: page > 1 ? 1 : 0.4 }}
        disabled={page <= 1} onClick={() => setPage(page - 1)}>← précédent</button>
      <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {page} / {pages}</span>
      <button style={{ ...champ, cursor: page < pages ? "pointer" : "default", opacity: page < pages ? 1 : 0.4 }}
        disabled={page >= pages} onClick={() => setPage(page + 1)}>suivant →</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LE JOURNAL
// ═══════════════════════════════════════════════════════════════════════
function Journal({ token }) {
  const [f, setF] = useState({ sens: "", joueur: "", serveur: "", depuis: "", jusqua: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/shop/journal?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, f, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement du journal...</div>;
  if (data && data.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const lignes = data.lignes || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={champ} value={f.sens} onChange={(e) => changer("sens", e.target.value)}>
          <option value="">tous sens confondus</option>
          {(data.sens || []).map((s) => (
            <option key={s.sens} value={s.sens}>
              {s.famille === "sortant" ? "↑ " : s.famille === "entrant" ? "↓ " : "? "}{s.sens} ({s.total})
            </option>
          ))}
        </select>
        <input style={{ ...champ, minWidth: 150 }} placeholder="pseudo ou UUID..."
          value={f.joueur} onChange={(e) => changer("joueur", e.target.value)} />
        <select style={champ} value={f.serveur} onChange={(e) => changer("serveur", e.target.value)}>
          <option value="">tous serveurs</option>
          {(data.serveurs || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input style={champ} type="date" value={f.depuis} onChange={(e) => changer("depuis", e.target.value)} />
        <input style={champ} type="date" value={f.jusqua} onChange={(e) => changer("jusqua", e.target.value)} />
        {(f.sens || f.joueur || f.serveur || f.depuis || f.jusqua) && (
          <button style={{ ...champ, cursor: "pointer", color: THEME.textMuted }}
            onClick={() => { setPage(1); setF({ sens: "", joueur: "", serveur: "", depuis: "", jusqua: "" }); }}>
            ✕ tout effacer
          </button>
        )}
      </div>

      {/* Les totaux portent sur le FILTRE, pas sur la page affichée. */}
      {data.somme && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Compteur label="Lignes" valeur={nombre(data.total)} />
          <Compteur label="Montant" valeur={argent(data.somme.montant)} detail="sur tout le filtre" />
          <Compteur label="Unités" valeur={nombre(data.somme.unites)} />
          <Compteur label="Joueurs" valeur={nombre(data.somme.joueurs)} />
        </div>
      )}

      {lignes.length === 0 ? <Vide texte="Aucun mouvement ne correspond." /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Quand</th>
                <th style={th}>Joueur</th>
                <th style={th}>Sens</th>
                <th style={th}>Article</th>
                <th style={{ ...th, textAlign: "right" }}>Qté</th>
                <th style={{ ...th, textAlign: "right" }}>P.U.</th>
                <th style={{ ...th, textAlign: "right" }}>Montant</th>
                <th style={th} title="Ce qui a réellement été appliqué à cette ligne : le prix recalculé de l'onglet Catalogue est le plein tarif.">Remise</th>
                <th style={th}>Serveur</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id}>
                  <td style={{ ...td, color: THEME.textMuted, whiteSpace: "nowrap" }}>{horodate(l.horodatage)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{l.pseudo || <code style={{ fontSize: 10 }}>{l.uuid}</code>}</td>
                  <td style={td}>
                    {/* La couleur vient de la FAMILLE, pas du libellé : ACHAT_PANIER
                        est un achat, VENTE_COFFRE une revente, et un libellé
                        inconnu reste gris plutôt que d'être coloré au hasard. */}
                    <Etiquette texte={l.sens}
                      couleur={l.famille === "sortant" ? THEME.amber : l.famille === "entrant" ? THEME.emerald : THEME.textMuted}
                      fond={l.famille === "sortant" ? THEME.amberDim : l.famille === "entrant" ? THEME.emeraldDim : THEME.surface} />
                  </td>
                  <td style={td}>
                    {libelleArticle(l)}
                    {l.article_id === null && (
                      <span style={{ color: THEME.amber, fontSize: 10 }}> · article supprimé du catalogue</span>
                    )}
                  </td>
                  <td style={tdNum}>{nombre(l.quantite)}</td>
                  <td style={{ ...tdNum, color: THEME.textMuted }}>{l.prix_unitaire === null ? "—" : argent(l.prix_unitaire)}</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{l.montant === null ? "—" : argent(l.montant)}</td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {l.taux_remise ? (
                        <Etiquette texte={`−${nombre(l.taux_remise * 100, 1)} % ${l.origine_remise || ""}`.trim()}
                          couleur={THEME.purple} fond={THEME.purpleDim} />
                      ) : null}
                      {l.quantite_vedette ? (
                        <Etiquette texte={`★ ${nombre(l.quantite_vedette)} u`}
                          couleur={THEME.amber} fond={THEME.amberDim} />
                      ) : null}
                      {!l.taux_remise && !l.quantite_vedette && (
                        <span style={{ color: THEME.textMuted, fontSize: 11 }}>plein tarif</span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...td, color: THEME.textMuted, fontSize: 11 }}>{l.serveur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={data.page} pages={data.pages} setPage={setPage} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LES REFUS — la table que personne n'a jamais lue
// ═══════════════════════════════════════════════════════════════════════
function Refus({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/shop/refus`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  if (data && data.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const lignes = data.lignes || [];
  if (lignes.length === 0) {
    return (
      <div style={{ color: THEME.emerald, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
        ✅ Aucun article refusé au dernier chargement. Le catalogue est passé entier.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ color: THEME.textMuted, fontSize: 12 }}>
        Le plugin écrit ici ce qu'il a écarté au démarrage. Un seul défaut de pack en refuse
        souvent cinquante d'un coup — d'où le regroupement par contrôle, qui empêche le
        deuxième défaut de se noyer dans le premier.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(data.par_controle || []).map((c) => (
          <Compteur key={c.controle} label={c.controle} valeur={nombre(c.total)}
            couleur={THEME.amber}
            detail={`${pluriel(c.articles, "article")} · ${c.dernier ? dateCourte(c.dernier) : "—"}`} />
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Quand</th>
              <th style={th}>Identifiant</th>
              <th style={th}>Contrôle</th>
              <th style={th}>Détail</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.id}>
                <td style={{ ...td, color: THEME.textMuted, whiteSpace: "nowrap" }}>{horodate(l.horodatage)}</td>
                <td style={td}><code style={{ fontSize: 11 }}>{l.identifiant}</code></td>
                <td style={td}><Etiquette texte={l.controle} couleur={THEME.amber} fond={THEME.amberDim} /></td>
                <td style={{ ...td, color: THEME.textMuted, fontSize: 11 }}>{l.detail || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LES PLAFONDS À VIE — réservé admin
// ═══════════════════════════════════════════════════════════════════════
function Plafonds({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/shop/plafonds`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  if (data && data.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const vie = data.vie || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Ce n'est pas un avertissement de style : cette table ne se
          reconstruit pas. Elle doit être sauvegardée. */}
      <div style={{ color: THEME.purple, background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, borderRadius: 8, padding: "12px 16px", fontSize: 12 }}>
        <b>⚠ <code>os_achat_vie</code> ne se reconstruit pas.</b> Une fois par joueur, à vie.
        Perdre une ligne, c'est un joueur qui rachète son seau de lave. Elle doit être dans
        la sauvegarde quotidienne au même titre que <code>oa_boite</code> et <code>oj_recompense_donnee</code>.
      </div>

      {data.depassements > 0 && (
        <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
          <b>⛔ {pluriel(data.depassements, "ligne")} au-dessus du plafond.</b> Le plafond est
          appliqué par le plugin : au-dessus, il a été contourné, ou le plafond a été abaissé
          après coup. Les deux méritent de savoir laquelle.
        </div>
      )}

      {(data.jour || []).length > 0 && (
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
            Achats plafonnés par jour
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {data.jour.map((j) => (
              <div key={j.jour} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 11 }}>
                <div style={{ color: THEME.textMuted, fontSize: 10 }}>{j.jour}</div>
                <div style={{ color: THEME.text, fontWeight: 700 }}>{nombre(j.unites)} u</div>
                <div style={{ color: THEME.textMuted, fontSize: 10 }}>{pluriel(j.joueurs, "joueur")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {vie.length === 0 ? <Vide texte="Aucun achat plafonné à vie n'a encore été fait." /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Joueur (UUID)</th>
                <th style={th}>Article</th>
                <th style={{ ...th, textAlign: "right" }}>Acheté</th>
                <th style={{ ...th, textAlign: "right" }}>Plafond</th>
                <th style={th}>État</th>
              </tr>
            </thead>
            <tbody>
              {vie.map((l) => (
                <tr key={`${l.uuid}-${l.article_id}`} style={{ background: l.depasse ? THEME.redDim : "transparent" }}>
                  <td style={td}><code style={{ fontSize: 10 }}>{l.uuid}</code></td>
                  <td style={td}>{libelleArticle(l)}</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{nombre(l.quantite)}</td>
                  <td style={{ ...tdNum, color: THEME.textMuted }}>{l.plafond_vie === null ? "—" : nombre(l.plafond_vie)}</td>
                  <td style={td}>
                    {l.depasse
                      ? <Etiquette texte="dépassé" couleur={THEME.red} fond={THEME.redDim} />
                      : l.plafond_vie !== null && l.quantite >= l.plafond_vie
                        ? <Etiquette texte="atteint" couleur={THEME.amber} fond={THEME.amberDim} />
                        : <span style={{ color: THEME.textMuted, fontSize: 11 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
